import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Download, Refresh } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import EmployeeCalendar from './EmployeeCalendar';
import EmployeesGrid from './EmployeesGrid';
import BandsPanel from './BandsPanel';
import GroupsPanel from './GroupsPanel';
import ManagerTeamsPanel from './ManagerTeamsPanel';
import TodayThisWeekPanel from './TodayThisWeekPanel';
import StaffTrainingHub from './StaffTrainingHub';
import { ROLES } from '../../constants/roles';
import { roleMatches } from '../../utils/rolePermissions';

const ATTENDANCE_STATUSES = ['PRESENT', 'LATE', 'ABSENT', 'HALF_DAY'];
const AVAIL_TYPES = ['LEAVE', 'UNAVAILABLE', 'TRAINING', 'MEDICAL', 'OTHER'];

function HrmsSuite() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [from, setFrom] = useState(dayjs().startOf('week').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().endOf('week').format('YYYY-MM-DD'));
  const [department, setDepartment] = useState('');
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [roster, setRoster] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [overtime, setOvertime] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [availOpen, setAvailOpen] = useState(false);
  const [otOpen, setOtOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    userId: '',
    shiftId: '',
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    skipWeekends: false,
    remarks: ''
  });
  const [assignSaving, setAssignSaving] = useState(false);
  const [shiftForm, setShiftForm] = useState({ name: '', code: '', startTime: '09:00', endTime: '18:00', breakMinutes: 60 });
  const [availForm, setAvailForm] = useState({ type: 'LEAVE', startDate: dayjs().format('YYYY-MM-DD'), endDate: dayjs().format('YYYY-MM-DD'), reason: '' });
  const [otForm, setOtForm] = useState({ date: dayjs().format('YYYY-MM-DD'), hours: 1, reason: '' });
  const [attForm, setAttForm] = useState({ userId: '', date: dayjs().format('YYYY-MM-DD'), status: 'PRESENT', remarks: '' });
  const TRAINING_TAB_INDEX = 8;

  useEffect(() => {
    const tabParam = (searchParams.get('tab') || '').toLowerCase();
    if (tabParam === 'training' || tabParam === String(TRAINING_TAB_INDEX)) {
      setTab(TRAINING_TAB_INDEX);
    }
  }, [searchParams]);

  const loadStatus = useCallback(async () => {
    const res = await axios.get('/api/hrms/status');
    setStatus(res.data);
    return res.data;
  }, []);

  const loadData = useCallback(async (st) => {
    if (!st?.enabled || !st?.canAccess) return;
    const params = { from, to };
    if (department) params.department = department;
    const [empRes, shiftRes, rosterRes, availRes, attRes, otRes] = await Promise.all([
      axios.get('/api/hrms/employees', { params: department ? { department } : {} }),
      axios.get('/api/hrms/shifts'),
      axios.get('/api/hrms/roster', { params }),
      axios.get('/api/hrms/availability', { params }),
      axios.get('/api/hrms/attendance', { params }),
      axios.get('/api/hrms/overtime', { params })
    ]);
    setEmployees(empRes.data || []);
    setShifts(shiftRes.data || []);
    setRoster(rosterRes.data || []);
    setAvailability(availRes.data || []);
    setAttendance(attRes.data || []);
    setOvertime(otRes.data || []);
  }, [from, to, department]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const st = await loadStatus();
      await loadData(st);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load Staff details', 'error');
    } finally {
      setLoading(false);
    }
  }, [loadStatus, loadData, showToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canAssign = useMemo(() => {
    if (!status) return false;
    if (typeof status.canAssign === 'boolean') return status.canAssign;
    return status.canViewAll || (Array.isArray(status.managedDepartments) && status.managedDepartments.length > 0);
  }, [status]);

  const staffCertsSelfId = useMemo(() => {
    if (!user?._id) return null;
    const isStaffManager = roleMatches(user.role, [
      ROLES.SUPER_ADMIN,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.HR_MANAGER,
      ROLES.SAFETY_COFS,
      ROLES.SAFETY_MANAGER,
      ROLES.SAFETY_OFFICER
    ]);
    if (isStaffManager || canAssign || status?.canViewAll || status?.canAssignTraining) return null;
    // Dept managers / team viewers keep multi-employee filter; plain employees lock to self
    const managed = status?.managedDepartments;
    if (Array.isArray(managed) && managed.length > 0) return null;
    return user._id;
  }, [user, canAssign, status]);

  const assignDayCount = useMemo(() => {
    if (!assignForm.startDate || !assignForm.endDate) return 0;
    let cursor = dayjs(assignForm.startDate);
    const end = dayjs(assignForm.endDate);
    if (!cursor.isValid() || !end.isValid() || end.isBefore(cursor, 'day')) return 0;
    let count = 0;
    while (!cursor.isAfter(end, 'day')) {
      const dow = cursor.day();
      if (!assignForm.skipWeekends || (dow !== 0 && dow !== 6)) count += 1;
      cursor = cursor.add(1, 'day');
    }
    return count;
  }, [assignForm.startDate, assignForm.endDate, assignForm.skipWeekends]);

  if (!loading && status && (!status.enabled || !status.canAccess)) {
    if (status.canAccessStaffTraining) {
      return <Navigate to="/staff-training" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (loading && !status) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const setAssignRangePreset = (preset) => {
    const today = dayjs();
    if (preset === 'today') {
      const d = today.format('YYYY-MM-DD');
      setAssignForm((f) => ({ ...f, startDate: d, endDate: d }));
      return;
    }
    if (preset === 'week') {
      setAssignForm((f) => ({
        ...f,
        startDate: today.startOf('week').format('YYYY-MM-DD'),
        endDate: today.endOf('week').format('YYYY-MM-DD')
      }));
      return;
    }
    if (preset === 'month') {
      setAssignForm((f) => ({
        ...f,
        startDate: today.startOf('month').format('YYYY-MM-DD'),
        endDate: today.endOf('month').format('YYYY-MM-DD')
      }));
    }
  };

  const onAssign = async () => {
    if (!assignForm.userId || !assignForm.shiftId) {
      showToast('Select employee and shift', 'warning');
      return;
    }
    if (!assignForm.startDate || !assignForm.endDate) {
      showToast('Select start and end dates', 'warning');
      return;
    }
    if (dayjs(assignForm.endDate).isBefore(dayjs(assignForm.startDate), 'day')) {
      showToast('End date must be on or after start date', 'warning');
      return;
    }
    if (!assignDayCount) {
      showToast('No dates to assign after weekend filter', 'warning');
      return;
    }
    setAssignSaving(true);
    try {
      const res = await axios.post('/api/hrms/roster', {
        userId: assignForm.userId,
        shiftId: assignForm.shiftId,
        startDate: assignForm.startDate,
        endDate: assignForm.endDate,
        skipWeekends: assignForm.skipWeekends,
        remarks: assignForm.remarks || undefined
      });
      const count = res.data?.count || 1;
      showToast(count > 1 ? `Shift assigned for ${count} days` : 'Shift assigned', 'success');
      setAssignOpen(false);
      await loadData(status);
    } catch (err) {
      showToast(err.response?.data?.message || 'Assign failed', 'error');
    } finally {
      setAssignSaving(false);
    }
  };

  const onCreateShift = async () => {
    try {
      await axios.post('/api/hrms/shifts', shiftForm);
      showToast('Shift created', 'success');
      setShiftOpen(false);
      await loadData(status);
    } catch (err) {
      showToast(err.response?.data?.message || 'Create shift failed', 'error');
    }
  };

  const onRequestAvail = async () => {
    try {
      await axios.post('/api/hrms/availability', availForm);
      showToast('Request submitted', 'success');
      setAvailOpen(false);
      await loadData(status);
    } catch (err) {
      showToast(err.response?.data?.message || 'Request failed', 'error');
    }
  };

  const onRequestOt = async () => {
    try {
      await axios.post('/api/hrms/overtime', otForm);
      showToast('OT request submitted', 'success');
      setOtOpen(false);
      await loadData(status);
    } catch (err) {
      showToast(err.response?.data?.message || 'OT request failed', 'error');
    }
  };

  const onMarkAttendance = async () => {
    try {
      const payload = { ...attForm };
      if (!payload.userId) payload.userId = user._id;
      await axios.post('/api/hrms/attendance', payload);
      showToast('Attendance saved', 'success');
      setAttOpen(false);
      await loadData(status);
    } catch (err) {
      showToast(err.response?.data?.message || 'Attendance failed', 'error');
    }
  };

  const review = async (kind, id, reviewStatus) => {
    try {
      await axios.post(`/api/hrms/${kind}/${id}/review`, { status: reviewStatus });
      showToast(`${reviewStatus}`, 'success');
      await loadData(status);
    } catch (err) {
      showToast(err.response?.data?.message || 'Review failed', 'error');
    }
  };

  const exportCsv = async (type) => {
    try {
      const res = await axios.get(`/api/hrms/export/${type}`, {
        params: { from, to, department: department || undefined },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `hrms-${type}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err.response?.data?.message || 'Export failed', 'error');
    }
  };

  const cancelRoster = async (id) => {
    try {
      await axios.delete(`/api/hrms/roster/${id}`);
      showToast('Roster cancelled', 'success');
      await loadData(status);
    } catch (err) {
      showToast(err.response?.data?.message || 'Cancel failed', 'error');
    }
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5">Staff details</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button startIcon={<Refresh />} onClick={refresh}>Refresh</Button>
          {status?.canManageShifts && (
            <Button variant="outlined" startIcon={<Add />} onClick={() => setShiftOpen(true)}>New shift</Button>
          )}
          {canAssign && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setAssignOpen(true)}>Assign shift</Button>
          )}
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Ground staff only. CREW / flight duty stays in Crew Duty Roster. Department managers assign their own people.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          {(status?.canViewAll || (status?.managedDepartments?.length > 1)) && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {(status?.departments || []).map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button startIcon={<Download />} onClick={() => exportCsv('roster')}>Roster CSV</Button>
          <Button startIcon={<Download />} onClick={() => exportCsv('attendance')}>Attendance CSV</Button>
          <Button startIcon={<Download />} onClick={() => exportCsv('overtime')}>OT CSV</Button>
        </Stack>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Today & this week" />
        <Tab label="Employees" />
        <Tab label="Employee calendar" />
        <Tab label="Roster" />
        <Tab label="Shifts" />
        <Tab label="Availability" />
        <Tab label="Attendance" />
        <Tab label="Overtime" />
        <Tab label="Training" />
        <Tab label="Bands" />
        <Tab label="Groups" />
        <Tab label="Manager teams" />
      </Tabs>

      {tab === 0 && (
        <TodayThisWeekPanel
          canViewTeam={Boolean(
            status?.canViewAll
            || (Array.isArray(status?.managedDepartments) && status.managedDepartments.length > 0)
            || status?.canAssign
          )}
        />
      )}

      {tab === 1 && (
        <EmployeesGrid
          employees={employees}
          shifts={shifts}
          canAssign={canAssign}
          canManageBands={Boolean(status?.canManageBands)}
          canManageGroups={Boolean(status?.canManageGroups)}
          canAssignTraining={Boolean(status?.canAssignTraining)}
          defaultFrom={from}
          defaultTo={to}
          onChanged={() => loadData(status)}
        />
      )}

      {tab === 2 && (
        <EmployeeCalendar
          employees={employees}
          shifts={shifts}
          canAssign={canAssign}
          managedDepartments={status?.managedDepartments}
          deptManagerAssignments={status?.deptManagerAssignments || []}
          currentUserId={user?._id}
          onMarked={() => loadData(status)}
        />
      )}

      {tab === 3 && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Time</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {roster.map((r) => (
                <TableRow key={r._id}>
                  <TableCell>{r.date ? dayjs(r.date).format('YYYY-MM-DD') : ''}</TableCell>
                  <TableCell>{r.userId ? `${r.userId.firstName} ${r.userId.lastName}` : ''}</TableCell>
                  <TableCell>{r.department}</TableCell>
                  <TableCell>{r.shiftId?.name}</TableCell>
                  <TableCell>{r.shiftId ? `${r.shiftId.startTime}–${r.shiftId.endTime}` : ''}</TableCell>
                  <TableCell>
                    {canAssign && (
                      <Button size="small" color="error" onClick={() => cancelRoster(r._id)}>Cancel</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!roster.length && (
                <TableRow><TableCell colSpan={6}>No roster entries in range</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 4 && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Break (min)</TableCell>
                <TableCell>OT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shifts.map((s) => (
                <TableRow key={s._id}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.code}</TableCell>
                  <TableCell>{s.startTime}</TableCell>
                  <TableCell>{s.endTime}</TableCell>
                  <TableCell>{s.breakMinutes}</TableCell>
                  <TableCell>{s.otEligible ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
              {!shifts.length && (
                <TableRow><TableCell colSpan={6}>No shifts yet — HR Manager can create them</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 5 && (
        <Box>
          <Button sx={{ mb: 1 }} variant="outlined" onClick={() => setAvailOpen(true)}>Request leave / unavailable</Button>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {availability.map((a) => (
                  <TableRow key={a._id}>
                    <TableCell>{a.userId ? `${a.userId.firstName} ${a.userId.lastName}` : ''}</TableCell>
                    <TableCell>{a.type}</TableCell>
                    <TableCell>{dayjs(a.startDate).format('YYYY-MM-DD')}</TableCell>
                    <TableCell>{dayjs(a.endDate).format('YYYY-MM-DD')}</TableCell>
                    <TableCell><Chip size="small" label={a.status} /></TableCell>
                    <TableCell>
                      {canAssign && a.status === 'PENDING' && (
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={() => review('availability', a._id, 'APPROVED')}>Approve</Button>
                          <Button size="small" color="error" onClick={() => review('availability', a._id, 'REJECTED')}>Reject</Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!availability.length && (
                  <TableRow><TableCell colSpan={6}>No availability requests</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {tab === 6 && (
        <Box>
          <Button sx={{ mb: 1 }} variant="outlined" onClick={() => setAttOpen(true)}>Mark attendance</Button>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendance.map((a) => (
                  <TableRow key={a._id}>
                    <TableCell>{dayjs(a.date).format('YYYY-MM-DD')}</TableCell>
                    <TableCell>{a.userId ? `${a.userId.firstName} ${a.userId.lastName}` : ''}</TableCell>
                    <TableCell>{a.department}</TableCell>
                    <TableCell>{a.status}</TableCell>
                    <TableCell>{a.remarks}</TableCell>
                  </TableRow>
                ))}
                {!attendance.length && (
                  <TableRow><TableCell colSpan={5}>No attendance records</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {tab === 7 && (
        <Box>
          <Button sx={{ mb: 1 }} variant="outlined" onClick={() => setOtOpen(true)}>Request overtime</Button>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {overtime.map((o) => (
                  <TableRow key={o._id}>
                    <TableCell>{dayjs(o.date).format('YYYY-MM-DD')}</TableCell>
                    <TableCell>{o.userId ? `${o.userId.firstName} ${o.userId.lastName}` : ''}</TableCell>
                    <TableCell>{o.hours}</TableCell>
                    <TableCell><Chip size="small" label={o.status} /></TableCell>
                    <TableCell>{o.reason}</TableCell>
                    <TableCell>
                      {canAssign && o.status === 'PENDING' && (
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={() => review('overtime', o._id, 'APPROVED')}>Approve</Button>
                          <Button size="small" color="error" onClick={() => review('overtime', o._id, 'REJECTED')}>Reject</Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!overtime.length && (
                  <TableRow><TableCell colSpan={6}>No overtime requests</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {tab === 8 && (
        <StaffTrainingHub
          employees={employees}
          selfCrewId={staffCertsSelfId}
          showAssignments
          title="Training & certificates"
          subtitle="Organised workspaces for profile certificates, profile trainings, and time-bound assignments."
          assignmentProps={{
            canAssignTraining: Boolean(status?.canAssignTraining),
            canAssign,
            department,
            from,
            to,
            currentUserId: user?._id
          }}
        />
      )}

      {tab === 9 && (
        <BandsPanel
          employees={employees}
          canManageBands={Boolean(status?.canManageBands)}
          department={department}
          departments={status?.departments || []}
        />
      )}

      {tab === 10 && (
        <GroupsPanel
          employees={employees}
          canManageGroups={Boolean(status?.canManageGroups)}
          department={department}
          departments={status?.departments || []}
          managedDepartments={status?.managedDepartments}
        />
      )}

      {tab === 11 && (
        <ManagerTeamsPanel
          employees={employees}
          canManageManagerTeams={Boolean(status?.canManageManagerTeams)}
          enforceManagerTeams={Boolean(status?.enforceManagerTeams)}
          department={department}
          departments={status?.departments || []}
        />
      )}

      <Dialog open={assignOpen} onClose={() => !assignSaving && setAssignOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assign shift</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                label="Employee"
                value={assignForm.userId}
                onChange={(e) => setAssignForm({ ...assignForm, userId: e.target.value })}
              >
                {employees.map((e) => (
                  <MenuItem key={e._id} value={e._id}>
                    {e.firstName} {e.lastName} ({e.department || e.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Shift</InputLabel>
              <Select
                label="Shift"
                value={assignForm.shiftId}
                onChange={(e) => setAssignForm({ ...assignForm, shiftId: e.target.value })}
              >
                {shifts.map((s) => (
                  <MenuItem key={s._id} value={s._id}>{s.name} ({s.startTime}–{s.endTime})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" onClick={() => setAssignRangePreset('today')}>Today</Button>
              <Button size="small" variant="outlined" onClick={() => setAssignRangePreset('week')}>This week</Button>
              <Button size="small" variant="outlined" onClick={() => setAssignRangePreset('month')}>This month</Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={assignForm.startDate}
                onChange={(e) => setAssignForm({
                  ...assignForm,
                  startDate: e.target.value,
                  endDate: assignForm.endDate < e.target.value ? e.target.value : assignForm.endDate
                })}
                fullWidth
              />
              <TextField
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={assignForm.endDate}
                onChange={(e) => setAssignForm({ ...assignForm, endDate: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={assignForm.skipWeekends}
                  onChange={(e) => setAssignForm({ ...assignForm, skipWeekends: e.target.checked })}
                />
              )}
              label="Skip weekends (Sat–Sun)"
            />
            <Typography variant="body2" color="text.secondary">
              {assignDayCount === 1
                ? 'Will assign for 1 day'
                : `Will assign for ${assignDayCount} days`}
              {assignDayCount > 31 ? ' (large range — confirm before saving)' : ''}
            </Typography>
            <TextField
              label="Remarks"
              value={assignForm.remarks}
              onChange={(e) => setAssignForm({ ...assignForm, remarks: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={assignSaving}>Cancel</Button>
          <Button variant="contained" onClick={onAssign} disabled={assignSaving || !assignDayCount}>
            {assignSaving ? 'Assigning…' : assignDayCount > 1 ? `Assign (${assignDayCount} days)` : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shiftOpen} onClose={() => setShiftOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New shift</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={shiftForm.name} onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })} fullWidth />
            <TextField label="Code" value={shiftForm.code} onChange={(e) => setShiftForm({ ...shiftForm, code: e.target.value })} fullWidth />
            <TextField label="Start (HH:mm)" value={shiftForm.startTime} onChange={(e) => setShiftForm({ ...shiftForm, startTime: e.target.value })} fullWidth />
            <TextField label="End (HH:mm)" value={shiftForm.endTime} onChange={(e) => setShiftForm({ ...shiftForm, endTime: e.target.value })} fullWidth />
            <TextField label="Break minutes" type="number" value={shiftForm.breakMinutes} onChange={(e) => setShiftForm({ ...shiftForm, breakMinutes: Number(e.target.value) })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreateShift}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={availOpen} onClose={() => setAvailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Availability / leave request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={availForm.type} onChange={(e) => setAvailForm({ ...availForm, type: e.target.value })}>
                {AVAIL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Start" type="date" InputLabelProps={{ shrink: true }} value={availForm.startDate} onChange={(e) => setAvailForm({ ...availForm, startDate: e.target.value })} fullWidth />
            <TextField label="End" type="date" InputLabelProps={{ shrink: true }} value={availForm.endDate} onChange={(e) => setAvailForm({ ...availForm, endDate: e.target.value })} fullWidth />
            <TextField label="Reason" value={availForm.reason} onChange={(e) => setAvailForm({ ...availForm, reason: e.target.value })} fullWidth multiline />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onRequestAvail}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={otOpen} onClose={() => setOtOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Overtime request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Date" type="date" InputLabelProps={{ shrink: true }} value={otForm.date} onChange={(e) => setOtForm({ ...otForm, date: e.target.value })} fullWidth />
            <TextField label="Hours" type="number" inputProps={{ min: 0.25, step: 0.25 }} value={otForm.hours} onChange={(e) => setOtForm({ ...otForm, hours: Number(e.target.value) })} fullWidth />
            <TextField label="Reason" value={otForm.reason} onChange={(e) => setOtForm({ ...otForm, reason: e.target.value })} fullWidth multiline />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onRequestOt}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={attOpen} onClose={() => setAttOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Mark attendance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {canAssign && (
              <FormControl fullWidth>
                <InputLabel>Employee</InputLabel>
                <Select
                  label="Employee"
                  value={attForm.userId || user?._id || ''}
                  onChange={(e) => setAttForm({ ...attForm, userId: e.target.value })}
                >
                  {employees.map((e) => (
                    <MenuItem key={e._id} value={e._id}>{e.firstName} {e.lastName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField label="Date" type="date" InputLabelProps={{ shrink: true }} value={attForm.date} onChange={(e) => setAttForm({ ...attForm, date: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={attForm.status} onChange={(e) => setAttForm({ ...attForm, status: e.target.value })}>
                {ATTENDANCE_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Remarks" value={attForm.remarks} onChange={(e) => setAttForm({ ...attForm, remarks: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onMarkAttendance}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HrmsSuite;
